import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useAuthStatus } from '@/hooks/useApi';
import {
  LayoutDashboard,
  Building2,
  CheckSquare,
  Menu,
  X,
  Settings,
  Users,
  UserCheck,
  Calendar,
  ChevronDown,
  Cog,
  FileText,
  FileCode,
  HelpCircle,
  Eye,
  BarChart3,
  Key,
  Scale,
  TrendingUp,
  ShieldCheck,
  ArrowLeftRight,
  LineChart,
  Landmark,
  Sliders,
} from 'lucide-react';
import { cn } from '@/utils/cn';
import UserMenu from './UserMenu';
import { NotificationBell } from '@/components/notifications';
import { useModuleContext } from '@/contexts/ModuleContext';

interface NavigationItem {
  name: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
  module?: string;
}

const navigation: NavigationItem[] = [
  {
    name: 'Dashboard',
    href: '/dashboard',
    icon: LayoutDashboard,
  },
  {
    name: 'Accounts',
    href: '/accounts',
    icon: Building2,
  },
  {
    name: 'Reconciliation',
    href: '/reconciliation',
    icon: CheckSquare,
  },
  {
    name: 'GRNI',
    href: '/sbl/grni',
    icon: ArrowLeftRight,
    module: 'sbl',
  },
  {
    name: 'GP Reporting',
    href: '/sbl/gp-reporting',
    icon: LineChart,
    module: 'sbl',
  },
  {
    name: 'Bank Rec',
    href: '/bankrec',
    icon: Landmark,
    module: 'bankrec',
  },
];

const Navbar: React.FC = () => {
  const location = useLocation();
  const { data: authStatus } = useAuthStatus();
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
  const [adminMenuOpen, setAdminMenuOpen] = React.useState(false);
  const [reportsMenuOpen, setReportsMenuOpen] = React.useState(false);
  
  // Check if user has admin role
  const isAdmin = authStatus?.user?.roles?.includes('admin') || false;

  const { hasModule } = useModuleContext();
  const visibleNavigation = navigation.filter(item => !item.module || hasModule(item.module));
  
  // Close menus when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.admin-dropdown')) {
        setAdminMenuOpen(false);
      }
      if (!target.closest('.reports-dropdown')) {
        setReportsMenuOpen(false);
      }
    };
    
    if (adminMenuOpen || reportsMenuOpen) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [adminMenuOpen, reportsMenuOpen]);

  return (
    <nav className="bg-black shadow-lg">
      <div className="px-6">
        <div className="flex justify-between h-16">
          {/* Logo and navigation */}
          <div className="flex items-center">
            {/* Logo */}
            <div className="flex-shrink-0 flex items-center">
              <img 
                className="h-12 w-auto" 
                src="/assets/clarative-480.png" 
                alt="Clarative" 
              />
            </div>
            
            {/* Desktop navigation */}
            <div className="hidden sm:ml-8 sm:flex sm:space-x-4">
              {visibleNavigation.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.href;
                
                return (
                  <NavLink
                    key={item.name}
                    to={item.href}
                    className={cn(
                      'inline-flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors duration-200',
                      isActive
                        ? 'bg-gray-900 text-white'
                        : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                    )}
                  >
                    <Icon className="h-4 w-4 mr-2" />
                    {item.name}
                  </NavLink>
                );
              })}
              
              {/* Reports dropdown menu - only for admins */}
              {isAdmin && (
                <div className="relative reports-dropdown">
                  <button
                    onClick={() => setReportsMenuOpen(!reportsMenuOpen)}
                    className={cn(
                      'inline-flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors duration-200',
                      reportsMenuOpen || location.pathname.startsWith('/reports')
                        ? 'bg-gray-900 text-white'
                        : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                    )}
                  >
                    <BarChart3 className="h-4 w-4 mr-2" />
                    Reports
                    <ChevronDown className="h-4 w-4 ml-1" />
                  </button>
                  
                  {reportsMenuOpen && (
                    <div className="absolute z-[100] left-0 mt-2 w-56 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5">
                      <div className="py-1">
                        <NavLink
                          to="/reports/executive-dashboard"
                          className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                          onClick={() => setReportsMenuOpen(false)}
                        >
                          <Eye className="h-4 w-4 mr-2" />
                          Executive Dashboard
                        </NavLink>
                        <NavLink
                          to="/reports/staff-performance"
                          className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                          onClick={() => setReportsMenuOpen(false)}
                        >
                          <TrendingUp className="h-4 w-4 mr-2" />
                          Staff Performance
                        </NavLink>
                      </div>
                    </div>
                  )}
                </div>
              )}
              
              {/* Admin dropdown menu */}
              {isAdmin && (
                <div className="relative admin-dropdown">
                  <button
                    onClick={() => setAdminMenuOpen(!adminMenuOpen)}
                    className={cn(
                      'inline-flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors duration-200',
                      adminMenuOpen || location.pathname.startsWith('/admin')
                        ? 'bg-gray-900 text-white'
                        : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                    )}
                  >
                    <Settings className="h-4 w-4 mr-2" />
                    Admin
                    <ChevronDown className="h-4 w-4 ml-1" />
                  </button>
                  
                  {adminMenuOpen && (
                    <div className="absolute z-[100] left-0 mt-2 w-56 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5">
                      <div className="py-1">
                        <NavLink
                          to="/admin/users"
                          className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                          onClick={() => setAdminMenuOpen(false)}
                        >
                          <Users className="h-4 w-4 mr-2" />
                          User Management
                        </NavLink>
                        <NavLink
                          to="/admin/assignments"
                          className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                          onClick={() => setAdminMenuOpen(false)}
                        >
                          <UserCheck className="h-4 w-4 mr-2" />
                          User Assignments
                        </NavLink>
                        <NavLink
                          to="/admin/periods"
                          className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                          onClick={() => setAdminMenuOpen(false)}
                        >
                          <Calendar className="h-4 w-4 mr-2" />
                          Period Management
                        </NavLink>
                        <NavLink
                          to="/admin/accounts"
                          className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                          onClick={() => setAdminMenuOpen(false)}
                        >
                          <Cog className="h-4 w-4 mr-2" />
                          Account Configuration
                        </NavLink>
                        <NavLink
                          to="/admin/auto-cert-rules"
                          className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                          onClick={() => setAdminMenuOpen(false)}
                        >
                          <ShieldCheck className="h-4 w-4 mr-2" />
                          Auto-Cert Rules
                        </NavLink>
                        <NavLink
                          to="/admin/templates"
                          className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                          onClick={() => setAdminMenuOpen(false)}
                        >
                          <FileCode className="h-4 w-4 mr-2" />
                          Template Management
                        </NavLink>
                        <NavLink
                          to="/admin/audit"
                          className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                          onClick={() => setAdminMenuOpen(false)}
                        >
                          <FileText className="h-4 w-4 mr-2" />
                          Audit Trail
                        </NavLink>
                        <NavLink
                          to="/admin/api-keys"
                          className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                          onClick={() => setAdminMenuOpen(false)}
                        >
                          <Key className="h-4 w-4 mr-2" />
                          API Keys
                        </NavLink>
                        {hasModule('bankrec') && (
                          <NavLink
                            to="/admin/bank-accounts"
                            className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                            onClick={() => setAdminMenuOpen(false)}
                          >
                            <Landmark className="h-4 w-4 mr-2" />
                            Bank Accounts
                          </NavLink>
                        )}
                        {hasModule('bankrec') && (
                          <NavLink
                            to="/admin/bankrec-settings"
                            className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                            onClick={() => setAdminMenuOpen(false)}
                          >
                            <Sliders className="h-4 w-4 mr-2" />
                            Bank Rec Settings
                          </NavLink>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
              
              {/* Help link - moved to end */}
              <NavLink
                to="/help"
                className={cn(
                  'inline-flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors duration-200',
                  location.pathname.startsWith('/help')
                    ? 'bg-gray-900 text-white'
                    : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                )}
              >
                <HelpCircle className="h-4 w-4 mr-2" />
                Help
              </NavLink>
            </div>
          </div>

          {/* User menu and notifications */}
          <div className="flex items-center gap-2">
            {authStatus?.user && (
              <div className="hidden sm:flex sm:items-center gap-4">
                <NotificationBell />
                <UserMenu />
              </div>
            )}
            
            {/* Mobile menu button */}
            <div className="flex sm:hidden">
              <button
                type="button"
                className="inline-flex items-center justify-center p-2 rounded-md text-gray-400 hover:text-white hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              >
                <span className="sr-only">Open main menu</span>
                {mobileMenuOpen ? (
                  <X className="block h-6 w-6" />
                ) : (
                  <Menu className="block h-6 w-6" />
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileMenuOpen && (
        <div className="sm:hidden bg-gray-900">
          <div className="px-2 pt-2 pb-3 space-y-1">
            {navigation.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.href;
              
              return (
                <NavLink
                  key={item.name}
                  to={item.href}
                  className={cn(
                    'flex items-center px-3 py-2 text-base font-medium rounded-md',
                    isActive
                      ? 'bg-gray-800 text-white'
                      : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                  )}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <Icon className="h-5 w-5 mr-3" />
                  {item.name}
                </NavLink>
              );
            })}
            
            {/* Executive Dashboard in mobile menu */}
            {isAdmin && (
              <NavLink
                to="/executive-dashboard"
                className={cn(
                  'flex items-center px-3 py-2 text-base font-medium rounded-md',
                  location.pathname === '/executive-dashboard'
                    ? 'bg-gray-800 text-white'
                    : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                )}
                onClick={() => setMobileMenuOpen(false)}
              >
                <Eye className="h-5 w-5 mr-3" />
                Executive View
              </NavLink>
            )}
            
            {/* Admin section in mobile menu */}
            {isAdmin && (
              <>
                <div className="mt-4 mb-2 px-3 text-xs font-semibold text-gray-400 uppercase tracking-wider">
                  Administration
                </div>
                <NavLink
                  to="/admin/users"
                  className={cn(
                    'flex items-center px-3 py-2 text-base font-medium rounded-md',
                    location.pathname === '/admin/users'
                      ? 'bg-gray-800 text-white'
                      : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                  )}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <Users className="h-5 w-5 mr-3" />
                  User Management
                </NavLink>
                <NavLink
                  to="/admin/assignments"
                  className={cn(
                    'flex items-center px-3 py-2 text-base font-medium rounded-md',
                    location.pathname === '/admin/assignments'
                      ? 'bg-gray-800 text-white'
                      : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                  )}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <UserCheck className="h-5 w-5 mr-3" />
                  User Assignments
                </NavLink>
                <NavLink
                  to="/admin/periods"
                  className={cn(
                    'flex items-center px-3 py-2 text-base font-medium rounded-md',
                    location.pathname === '/admin/periods'
                      ? 'bg-gray-800 text-white'
                      : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                  )}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <Calendar className="h-5 w-5 mr-3" />
                  Period Management
                </NavLink>
                <NavLink
                  to="/admin/accounts"
                  className={cn(
                    'flex items-center px-3 py-2 text-base font-medium rounded-md',
                    location.pathname === '/admin/accounts'
                      ? 'bg-gray-800 text-white'
                      : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                  )}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <Cog className="h-5 w-5 mr-3" />
                  Account Configuration
                </NavLink>
                <NavLink
                  to="/admin/auto-cert-rules"
                  className={cn(
                    'flex items-center px-3 py-2 text-base font-medium rounded-md',
                    location.pathname === '/admin/auto-cert-rules'
                      ? 'bg-gray-800 text-white'
                      : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                  )}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <ShieldCheck className="h-5 w-5 mr-3" />
                  Auto-Cert Rules
                </NavLink>
                <NavLink
                  to="/admin/templates"
                  className={cn(
                    'flex items-center px-3 py-2 text-base font-medium rounded-md',
                    location.pathname === '/admin/templates'
                      ? 'bg-gray-800 text-white'
                      : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                  )}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <FileCode className="h-5 w-5 mr-3" />
                  Template Management
                </NavLink>
                <NavLink
                  to="/admin/audit"
                  className={cn(
                    'flex items-center px-3 py-2 text-base font-medium rounded-md',
                    location.pathname === '/admin/audit'
                      ? 'bg-gray-800 text-white'
                      : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                  )}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <FileText className="h-5 w-5 mr-3" />
                  Audit Trail
                </NavLink>
              </>
            )}
            
            {/* Help link in mobile menu */}
            <NavLink
              to="/help"
              className={cn(
                'flex items-center px-3 py-2 text-base font-medium rounded-md',
                location.pathname.startsWith('/help')
                  ? 'bg-gray-800 text-white'
                  : 'text-gray-300 hover:bg-gray-700 hover:text-white'
              )}
              onClick={() => setMobileMenuOpen(false)}
            >
              <HelpCircle className="h-5 w-5 mr-3" />
              Help
            </NavLink>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;